import { EQUIPMENT_CATEGORIES, type EquipmentCategory, type InventoryFilters } from '@mads/shared'

/**
 * The inventory filters as they travel in the URL (spec W-02). Keeping them in
 * the query string is what lets another screen link straight to a filtered list
 * — "View items" on a location — and what makes the back button walk through
 * filter changes.
 *
 * Every key is optional and omitted when the filter is open, so an unfiltered
 * list keeps a bare `/inventory`.
 */
export interface InventorySearch {
  q?: string
  category?: EquipmentCategory
  locationId?: number
  fixtureModelId?: number
}

/**
 * Anything can arrive in a query string — hand-typed, stale, or from an old
 * bookmark. Unrecognised values are dropped rather than rejected: a bad
 * `category` leaves that filter open instead of erroring the page.
 *
 * This is also what writes the URL (see `Inventory`), so the round trip only
 * has one set of rules.
 */
export function parseInventorySearch(search: Record<string, unknown>): InventorySearch {
  const parsed: InventorySearch = {}

  const { q, category, locationId, fixtureModelId } = search

  if (typeof q === 'string' && q !== '') parsed.q = q

  if (
    typeof category === 'string' &&
    (EQUIPMENT_CATEGORIES as readonly string[]).includes(category)
  ) {
    parsed.category = category as EquipmentCategory
  }

  // Numbers survive the default search parser as numbers, but a hand-written
  // URL gives a string; both mean the same location.
  const id = positiveId(locationId)
  if (id !== null) parsed.locationId = id

  // "Every item of this make and model" — how a fixture model links to its
  // own filtered inventory (issue #30).
  const modelId = positiveId(fixtureModelId)
  if (modelId !== null) parsed.fixtureModelId = modelId

  return parsed
}

function positiveId(value: unknown): number | null {
  const id = typeof value === 'number' || typeof value === 'string' ? Number(value) : NaN

  return Number.isInteger(id) && id > 0 ? id : null
}

/**
 * The API client wants every filter present, with `All` for "not filtered";
 * the URL wants the key absent. This is the one place that translates.
 */
export function toInventoryFilters(search: InventorySearch): InventoryFilters {
  return {
    q: search.q ?? '',
    category: search.category ?? 'All',
    locationId: search.locationId ?? 'All',
    fixtureModelId: search.fixtureModelId ?? 'All',
  }
}
